import pluralize from "pluralize";
import toast from "react-hot-toast";

import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {ANALYZER_TOAST_DURATION_MS} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerSetIntervalsModalProps} from "@/pages/analyzer/Analyzer.types.ts";
import {canApplyIntervalToProposal, getBulkIntervalBounds} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";
import {BulkSetIntervalsForm} from "@/pages/checks/list/actions/bulkSetIntervals/BulkSetIntervalsForm.tsx";
import {EditCheckIntervalsFormValues} from "@/pages/checks/list/actions/bulkSetIntervals/BulkSetIntervalsModal.tsx";

const FORM_ID = "analyzer-set-intervals-form";

export const AnalyzerSetIntervalsModal = ({show, setShow}: AnalyzerSetIntervalsModalProps) => {
  const {selectedProposals, updateProposedChecks, formData} = useAnalyzerContext();
  const bounds = formData ? getBulkIntervalBounds(formData, selectedProposals) : null;
  const intervals = selectedProposals.flatMap(({msp_interval}) => msp_interval ?? []);
  const lowestInterval = intervals.length ? Math.min(...intervals) : undefined;
  const defaultInterval =
    lowestInterval !== undefined && bounds
      ? Math.min(Math.max(lowestInterval, bounds.min), bounds.max)
      : lowestInterval;

  const handleSubmit = ({msp_interval}: EditCheckIntervalsFormValues) => {
    updateProposedChecks({msp_interval});
    const applied = formData
      ? selectedProposals.filter((check) => canApplyIntervalToProposal(formData, check, msp_interval)).length
      : selectedProposals.length;
    const skipped = selectedProposals.length - applied;
    if (applied) {
      const skippedNote = skipped
        ? ` Skipped ${pluralize("Check", skipped, true)} with a fixed or minimum Interval for their type.`
        : "";
      toast.success(
        `Interval set to ${msp_interval} ${pluralize("minute", msp_interval)} on ${pluralize("Check", applied, true)}.${skippedNote}`,
        {duration: ANALYZER_TOAST_DURATION_MS}
      );
    } else {
      toast.error("No Checks updated. This Interval is outside the allowed range for every selected Check.", {
        duration: ANALYZER_TOAST_DURATION_MS,
      });
    }
    setShow(false);
  };

  return (
    <FormModal
      formId={FORM_ID}
      onCancel={() => setShow(false)}
      show={show}
      title={`Set Intervals on ${pluralize("Check", selectedProposals.length, true)}`}
    >
      <BulkSetIntervalsForm
        formId={FORM_ID}
        onSubmit={handleSubmit}
        note="Note: Intervals on the selected Checks will be replaced with whatever you select below. Checks with a fixed or minimum Interval for their type keep it."
        defaultInterval={defaultInterval}
        min={bounds?.min}
        max={bounds?.max}
      />
    </FormModal>
  );
};
